import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { IRole } from '../../../types/role';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getRolesAsync } from './getRolesSlice';

interface IUpdateRoleRequest {
  roleId: string;
  name: string;
  description: string;
  permissions: string[];
}

interface IUpdateRoleResponse {
  status?: boolean;
  message?: string;
  data?: Omit<IRole, 'isDefault'>;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateRoleResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateRoleAsync = createAsyncThunk(
  'role/update',
  async (
    values: IUpdateRoleRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IUpdateRoleResponse>(
        `/roles/${values.roleId}`,
        values,
        config
      );

      dispatch(getRolesAsync());

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Role has been updated',
        })
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateRoleResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const roleUpdateSlice = createSlice({
  name: 'role/update',
  initialState,
  reducers: {
    resetRoleUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateRoleAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateRoleAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateRoleResponse;
      })
      .addCase(updateRoleAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateRoleResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetRoleUpdate } = roleUpdateSlice.actions;

export default roleUpdateSlice.reducer;
